// The booking status vocabulary, shared by every screen that shows a
// booking's status (the bookings page, a Contact's booking history).
//
// A status is the server's slug, unchanged: the six defaults below, or a
// tenant's custom status. Nothing is folded into anything else, so a
// status read from the server and sent back is the same status.

export const BOOKING_STATUS = {
  pending: 'pending',
  awaitingDeposit: 'awaiting_deposit',
  awaitingConfirmation: 'awaiting_confirmation',
  confirmed: 'confirmed',
  completed: 'completed',
  cancelled: 'cancelled',
}

// Display order for legends and filters.
export const DEFAULT_BOOKING_STATUSES = [
  BOOKING_STATUS.pending,
  BOOKING_STATUS.awaitingDeposit,
  BOOKING_STATUS.awaitingConfirmation,
  BOOKING_STATUS.confirmed,
  BOOKING_STATUS.completed,
  BOOKING_STATUS.cancelled,
]

// Names older screens used for a default status.
const ALIASES = { deposit_pending: BOOKING_STATUS.awaitingDeposit }

const STYLES = {
  pending: { bg: 'bg-[#fff8e1]', text: 'text-[#f57f17]', ticket: { bg: '#fff8e1', border: '#ffe3a3', text: '#b26a00' } },
  awaiting_deposit: { bg: 'bg-[#f3e5f5]', text: 'text-[#6a1b9a]', ticket: { bg: '#f3e5f5', border: '#dfc2e8', text: '#6a1b9a' } },
  awaiting_confirmation: { bg: 'bg-[#fff3e0]', text: 'text-[#b26a00]', ticket: { bg: '#fff3e0', border: '#ffd699', text: '#b26a00' } },
  confirmed: { bg: 'bg-[#e8f5e9]', text: 'text-[#2e7d32]', ticket: { bg: '#e8f5e9', border: '#b7dfbb', text: '#2e7d32' } },
  completed: { bg: 'bg-[#e3f2fd]', text: 'text-[#1565c0]', ticket: { bg: '#e3f2fd', border: '#bbdefb', text: '#1565c0' } },
  cancelled: { bg: 'bg-[#ffebee]', text: 'text-[#c62828]', ticket: { bg: '#ffebee', border: '#f5c2c8', text: '#c62828' } },
}

// A tenant's custom status has no colour of its own.
const CUSTOM_STYLE = { bg: 'bg-[#e8ecfb]', text: 'text-[#1535c8]', ticket: { bg: '#e8ecfb', border: '#c9d5ff', text: '#1535c8' } }

/** The status of a booking as the server sent it. A row with none is pending. */
export function fromServerStatus(status) {
  const slug = String(status ?? '').trim()
  if (!slug) return BOOKING_STATUS.pending
  return ALIASES[slug] || slug
}

/** The slug to send to the server for a status: the status itself, with an old alias resolved. */
export function toServerStatus(status) {
  return fromServerStatus(status)
}

/** Tailwind classes and calendar-ticket colours for a status. */
export function bookingStatusStyle(status) {
  return STYLES[fromServerStatus(status)] || CUSTOM_STYLE
}

/** A booking in one of these statuses is waiting for staff to confirm it. */
export function canConfirmBooking(status) {
  const slug = fromServerStatus(status)
  return (
    slug === BOOKING_STATUS.pending ||
    slug === BOOKING_STATUS.awaitingDeposit ||
    slug === BOOKING_STATUS.awaitingConfirmation
  )
}

export function isCancelledBooking(status) {
  return fromServerStatus(status) === BOOKING_STATUS.cancelled
}
